import React from 'react';
import clsx from 'clsx';

// Single source of truth for the "status pill" look — every page currently
// re-implements this inline with its own STATUS_CONFIG object, each picking
// slightly different colors for conceptually identical states (e.g. three
// different shades used for "active" across the Operator, Supervisor and
// Admin pages). A page still owns its own domain -> {label, tone} mapping
// (statuses mean different things on a GateSession vs a ScheduledAnnouncement
// vs a BeltAssignment), it just hands the result here for consistent
// rendering instead of also owning the pill markup and colors.
const TONE_CLASSES = {
  neutral: 'bg-gray-100 text-gray-600',
  info:    'bg-blue-100 text-blue-700',
  success: 'bg-success-100 text-success-700',
  warning: 'bg-warning-100 text-warning-800', // amber
  yellow:  'bg-yellow-100 text-yellow-700',   // distinct from warning/amber — see CR 5.1
  orange:  'bg-orange-100 text-orange-700',   // distinct from warning/amber — see CR 5.1
  danger:  'bg-danger-100 text-danger-600',
  purple:  'bg-purple-100 text-purple-700',
  teal:    'bg-teal-100 text-teal-700',
};

const DOT_CLASSES = {
  neutral: 'bg-gray-400',
  info:    'bg-blue-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  yellow:  'bg-yellow-400',
  orange:  'bg-orange-500',
  danger:  'bg-danger-500',
  purple:  'bg-purple-500',
  teal:    'bg-teal-500',
};

// `size="sm"` is for tight contexts (a sidebar list row, an inline summary
// line) where the default px-2/py-0.5 pill reads as too heavy next to small
// text.
const SIZE_CLASSES = {
  md: 'px-2 py-0.5',
  sm: 'px-1.5 py-0',
};

export default function StatusBadge({ label, tone = 'neutral', dot = false, pulse = false, size = 'md', className = '' }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 text-xs font-semibold rounded-full',
        TONE_CLASSES[tone] || TONE_CLASSES.neutral,
        SIZE_CLASSES[size] || SIZE_CLASSES.md,
        className
      )}
    >
      {dot && (
        <span className={clsx('w-1.5 h-1.5 rounded-full', DOT_CLASSES[tone] || DOT_CLASSES.neutral, pulse && 'animate-pulse')} />
      )}
      {label}
    </span>
  );
}
